import { useEffect, useRef, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { PenLine, Upload, Eraser, Trash2, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";

const MAX_W = 600;

/** Reduz a imagem para no máximo 600px de largura, PNG com fundo transparente. */
async function normalizeImage(file: File): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = url;
    });
    const scale = Math.min(1, MAX_W / img.width);
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext("2d")!.drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/png");
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Cadastro da assinatura de quem aprova propostas: desenha na tela ou sobe
 * uma imagem. Uma por pessoa e workspace; estampada no PDF na aprovação.
 */
export default function SignatureCard() {
  const utils = trpc.useUtils();
  const q = trpc.crm.proposals.mySignature.useQuery();
  const set = trpc.crm.proposals.setMySignature.useMutation({
    onSuccess: () => { toast.success("Assinatura salva"); utils.crm.proposals.mySignature.invalidate(); setDrawing(false); },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao salvar assinatura")),
  });
  const remove = trpc.crm.proposals.removeMySignature.useMutation({
    onSuccess: () => { toast.success("Assinatura removida"); utils.crm.proposals.mySignature.invalidate(); },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao remover assinatura")),
  });

  const [drawing, setDrawing] = useState(false);
  const [hasInk, setHasInk] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!drawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#111827";
    let down = false;
    const pos = (e: PointerEvent) => {
      const r = canvas.getBoundingClientRect();
      return { x: ((e.clientX - r.left) / r.width) * canvas.width, y: ((e.clientY - r.top) / r.height) * canvas.height };
    };
    const onDown = (e: PointerEvent) => { down = true; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); canvas.setPointerCapture(e.pointerId); };
    const onMove = (e: PointerEvent) => { if (!down) return; const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); setHasInk(true); };
    const onUp = () => { down = false; };
    canvas.addEventListener("pointerdown", onDown);
    canvas.addEventListener("pointermove", onMove);
    canvas.addEventListener("pointerup", onUp);
    canvas.addEventListener("pointerleave", onUp);
    return () => {
      canvas.removeEventListener("pointerdown", onDown);
      canvas.removeEventListener("pointermove", onMove);
      canvas.removeEventListener("pointerup", onUp);
      canvas.removeEventListener("pointerleave", onUp);
    };
  }, [drawing]);

  const clear = () => {
    const c = canvasRef.current;
    if (c) c.getContext("2d")!.clearRect(0, 0, c.width, c.height);
    setHasInk(false);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base"><PenLine className="h-4 w-4" /> Minha assinatura</CardTitle>
        <CardDescription>Estampada no PDF de cada proposta que você aprovar, junto com o seu nome e a data.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {q.data && !drawing && (
          <div className="rounded-md border bg-white p-4 flex items-center justify-center">
            <img src={q.data.imageDataUrl} alt="Sua assinatura" className="max-h-24" />
          </div>
        )}
        {drawing && (
          <div className="space-y-2">
            <canvas ref={canvasRef} width={600} height={200} className="w-full max-w-[600px] rounded-md border bg-white touch-none cursor-crosshair" />
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={clear}><Eraser className="h-3.5 w-3.5 mr-1.5" /> Limpar</Button>
              <Button size="sm" disabled={!hasInk || set.isPending} onClick={() => canvasRef.current && set.mutate({ imageDataUrl: canvasRef.current.toDataURL("image/png") })}>
                {set.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Check className="h-3.5 w-3.5 mr-1.5" />} Salvar
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setDrawing(false)}>Cancelar</Button>
            </div>
          </div>
        )}
        {!drawing && (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => { setHasInk(false); setDrawing(true); }}>
              <PenLine className="h-3.5 w-3.5 mr-1.5" /> {q.data ? "Desenhar outra" : "Desenhar"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={set.isPending}>
              <Upload className="h-3.5 w-3.5 mr-1.5" /> Enviar imagem
            </Button>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (!f) return;
                if (f.size > 2 * 1024 * 1024) { toast.error("Imagem maior que 2 MB"); return; }
                try { set.mutate({ imageDataUrl: await normalizeImage(f) }); } catch { toast.error("Não consegui ler a imagem"); }
              }}
            />
            {q.data && (
              <Button size="sm" variant="ghost" className="text-destructive" disabled={remove.isPending} onClick={() => remove.mutate()}>
                <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Remover
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
